import React, { useEffect, useRef, useState } from 'react';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { Button } from 'v3/components';
import { styles } from './styles';

function formatDateBR(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export default function TrainingCard({
  item,
  onCopyLink,
  onEditConfig,
  onCloseChecklist,
  onViewAnswers,
  viewAnswersLoading = false,
}) {
  const [openMenu, setOpenMenu] = useState(false);
  const [copied, setCopied] = useState(false);
  const menuRef = useRef(null);
  const copiedTimeoutRef = useRef(null);

  useEffect(() => {
    function handleOutsideClick(event) {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(event.target)) {
        setOpenMenu(false);
      }
    }

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) {
        window.clearTimeout(copiedTimeoutRef.current);
      }
    };
  }, []);

  const isActive = !!item?.isActive;
  const linkUrl = item?.linkUrl || '';
  const canCopy = !!linkUrl && isActive;
  const hasResponses = Number(item?.responsesCount || 0) > 0;

  function triggerCopiedFeedback() {
    setCopied(true);

    if (copiedTimeoutRef.current) {
      window.clearTimeout(copiedTimeoutRef.current);
    }

    copiedTimeoutRef.current = window.setTimeout(() => {
      setCopied(false);
      copiedTimeoutRef.current = null;
    }, 1800);
  }

  async function handleCopy() {
    if (!canCopy || !onCopyLink) return;

    const copiedSuccessfully = await onCopyLink(linkUrl);
    if (copiedSuccessfully) {
      triggerCopiedFeedback();
    }
  }

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.titleWrap}>
          <div style={styles.iconBox}>
            <MenuBookOutlinedIcon style={{ fontSize: 16 }} />
          </div>

          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={styles.titleRow}>
              <div style={styles.title}>{item?.title || 'Treinamento'}</div>
              <span style={styles.statusPill(isActive)}>
                {isActive ? 'Ativo' : 'Inativo'}
              </span>
            </div>
            <div style={styles.createdBy}>
              <span style={styles.createdByLabel}>Criado por:</span>{' '}
              {item?.createdBy || '—'}
            </div>
            <div style={styles.desc}>{item?.description || '—'}</div>
          </div>
        </div>

        <div ref={menuRef} style={styles.menuWrap}>
          <button
            type="button"
            style={styles.menuBtn}
            onClick={() => setOpenMenu(prev => !prev)}
          >
            <MoreVertOutlinedIcon />
          </button>

          {openMenu && (
            <div style={styles.menu}>
              <button
                type="button"
                style={{
                  ...styles.menuItem,
                  opacity: canCopy ? 1 : 0.5,
                  cursor: canCopy ? 'pointer' : 'not-allowed',
                }}
                onClick={() => {
                  if (!canCopy) return;
                  handleCopy();
                  setOpenMenu(false);
                }}
                disabled={!canCopy}
              >
                {copied ? (
                  <CheckRoundedIcon style={{ fontSize: 18 }} />
                ) : (
                  <ContentCopyOutlinedIcon style={{ fontSize: 18 }} />
                )}
                Copiar link
              </button>

              <button
                type="button"
                style={styles.menuItem}
                onClick={() => {
                  if (onEditConfig) onEditConfig();
                  setOpenMenu(false);
                }}
              >
                <SettingsOutlinedIcon style={{ fontSize: 18 }} />
                Editar Configurações
              </button>

              <button
                type="button"
                style={styles.menuItemDanger}
                onClick={() => {
                  if (onCloseChecklist) onCloseChecklist();
                  setOpenMenu(false);
                }}
              >
                <HighlightOffOutlinedIcon style={{ fontSize: 18 }} />
                Encerrar treinamento
              </button>
            </div>
          )}
        </div>
      </div>

      <div style={styles.infoGrid}>
        <div style={styles.infoCard}>
          <div style={styles.infoLabel}>
            <PeopleOutlineOutlinedIcon style={{ fontSize: 18 }} />
            Quantidade de respostas
          </div>
          <div style={styles.infoValue}>{item?.responsesCount ?? 0}</div>
        </div>

        <div style={styles.infoCard}>
          <div style={styles.infoLabel}>
            <CalendarTodayOutlinedIcon style={{ fontSize: 18 }} />
            Criado em
          </div>
          <div style={styles.infoValue}>{formatDateBR(item?.createdAt)}</div>
        </div>

        <div style={styles.infoCard}>
          <div style={styles.infoLabel}>
            <AccessTimeOutlinedIcon style={{ fontSize: 18 }} />
            Expira em
          </div>
          <div
            style={
              item?.expireStatus === 'expired'
                ? styles.infoValueWarn
                : styles.infoValue
            }
          >
            {item?.expireLabel || 'Sem expiração'}
          </div>
        </div>
      </div>

      <div style={styles.linkRow}>
        <input
          value={isActive ? linkUrl : 'Treinamento encerrado'}
          readOnly
          style={styles.linkInput}
        />
        <button
          type="button"
          style={{
            ...styles.copyBtn,
            opacity: canCopy ? 1 : 0.5,
            cursor: canCopy ? 'pointer' : 'not-allowed',
          }}
          onClick={handleCopy}
          disabled={!canCopy}
          title={copied ? 'Copiado!' : 'Copiar link'}
        >
          {copied ? (
            <CheckRoundedIcon style={{ fontSize: 20, color: '#059669' }} />
          ) : (
            <ContentCopyOutlinedIcon style={{ fontSize: 20 }} />
          )}
        </button>
      </div>

      <div style={styles.footer}>
        <Button
          variant="secondary"
          onClick={onViewAnswers}
          disabled={!hasResponses || viewAnswersLoading}
          style={{ minWidth: 132, height: 44, padding: '0 14px' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <VisibilityOutlinedIcon style={{ fontSize: 17 }} />
            {viewAnswersLoading ? 'Carregando...' : 'Ver Respostas'}
          </div>
        </Button>
      </div>
    </div>
  );
}
